/**
 * LEGEND CLUB API
 *
 * Functions to interact with student profile/legend data.
 * Updates the legend_data JSONB column in children table.
 */

import { supabase } from "./supabase";
import type { LegendData, Project, APIResponse } from "../types/tisa-coins";
import { initializeLegendData } from "./tisa-helpers";

// ============================================================================
// STUDENT FUNCTIONS - Profile Management
// ============================================================================

/**
 * Get legend data for a user (with fallback to default structure)
 */
export const getLegendData = async (
  userId: string,
): Promise<APIResponse<LegendData>> => {
  try {
    const { data, error } = await supabase
      .from("children")
      .select("legend_data")
      .eq("id", userId)
      .single();

    if (error) throw error;

    // If legend_data is null or empty, return default structure
    const legendData =
      (data.legend_data as unknown as LegendData) || initializeLegendData();

    return {
      success: true,
      data: legendData,
    };
  } catch (error: any) {
    console.error("Get legend data error:", error);
    return {
      success: false,
      error: error.message,
      message: "Failed to load profile data",
    };
  }
};

/**
 * Update student bio
 */
export const updateBio = async (
  userId: string,
  bio: string,
): Promise<APIResponse<LegendData>> => {
  try {
    // Validate bio length
    if (bio.length > 200) {
      return {
        success: false,
        error: "Bio too long",
        message: "Bio must be 200 characters or less",
      };
    }

    // Get current legend_data
    const { data: user, error: fetchError } = await supabase
      .from("children")
      .select("legend_data")
      .eq("id", userId)
      .single();

    if (fetchError) throw fetchError;

    // Merge update
    const currentData =
      (user.legend_data as unknown as LegendData) || initializeLegendData();
    const updatedData: LegendData = {
      ...currentData,
      bio,
    };

    // Update database
    const { data, error } = await supabase
      .from("children")
      .update({ legend_data: updatedData as any })
      .eq("id", userId)
      .select("legend_data")
      .single();

    if (error) throw error;

    return {
      success: true,
      data: data.legend_data as unknown as LegendData,
      message: "Bio updated successfully!",
    };
  } catch (error: any) {
    console.error("Update bio error:", error);
    return {
      success: false,
      error: error.message,
      message: "Failed to update bio",
    };
  }
};

/**
 * Update showcase badges (max 6)
 */
export const updateShowcaseBadges = async (
  userId: string,
  badgeIds: string[],
): Promise<APIResponse<LegendData>> => {
  try {
    // Validate max 6 badges
    if (badgeIds.length > 6) {
      return {
        success: false,
        error: "Too many badges",
        message: "You can showcase up to 6 badges",
      };
    }

    // Get current legend_data
    const { data: user, error: fetchError } = await supabase
      .from("children")
      .select("legend_data")
      .eq("id", userId)
      .single();

    if (fetchError) throw fetchError;

    // Merge update
    const currentData =
      (user.legend_data as unknown as LegendData) || initializeLegendData();
    const updatedData: LegendData = {
      ...currentData,
      showcase_badges: badgeIds,
    };

    // Update database
    const { data, error } = await supabase
      .from("children")
      .update({ legend_data: updatedData as any })
      .eq("id", userId)
      .select("legend_data")
      .single();

    if (error) throw error;

    return {
      success: true,
      data: data.legend_data as unknown as LegendData,
      message: "Showcase badges updated!",
    };
  } catch (error: any) {
    console.error("Update showcase badges error:", error);
    return {
      success: false,
      error: error.message,
      message: "Failed to update showcase badges",
    };
  }
};

/**
 * Add a new project to profile
 */
export const addProject = async (
  userId: string,
  project: Omit<Project, "id" | "created_at">,
): Promise<APIResponse<LegendData>> => {
  try {
    // Validate project title
    if (!project.title || project.title.length === 0) {
      return {
        success: false,
        error: "Title required",
        message: "Project title is required",
      };
    }

    if (project.title.length > 100) {
      return {
        success: false,
        error: "Title too long",
        message: "Project title must be 100 characters or less",
      };
    }

    // Get current legend_data
    const { data: user, error: fetchError } = await supabase
      .from("children")
      .select("legend_data")
      .eq("id", userId)
      .single();

    if (fetchError) throw fetchError;

    // Create new project with ID and timestamp
    const newProject: Project = {
      ...project,
      id: crypto.randomUUID(),
      created_at: new Date().toISOString(),
    };

    // Merge update
    const currentData =
      (user.legend_data as unknown as LegendData) || initializeLegendData();
    const updatedData: LegendData = {
      ...currentData,
      showcase_projects: [...(currentData.showcase_projects || []), newProject],
    };

    // Update database
    const { data, error } = await supabase
      .from("children")
      .update({ legend_data: updatedData as any })
      .eq("id", userId)
      .select("legend_data")
      .single();

    if (error) throw error;

    return {
      success: true,
      data: data.legend_data as unknown as LegendData,
      message: "Project added to your profile!",
    };
  } catch (error: any) {
    console.error("Add project error:", error);
    return {
      success: false,
      error: error.message,
      message: "Failed to add project",
    };
  }
};

/**
 * Update project showcase status
 */
export const toggleProjectShowcase = async (
  userId: string,
  projectId: string,
  showcase: boolean,
): Promise<APIResponse<LegendData>> => {
  try {
    // Get current legend_data
    const { data: user, error: fetchError } = await supabase
      .from("children")
      .select("legend_data")
      .eq("id", userId)
      .single();

    if (fetchError) throw fetchError;

    const currentData =
      (user.legend_data as unknown as LegendData) || initializeLegendData();

    // Update project showcase status
    const updatedProjects = (currentData.showcase_projects || []).map((proj) =>
      proj.id === projectId ? { ...proj, showcase } : proj,
    );

    const updatedData: LegendData = {
      ...currentData,
      showcase_projects: updatedProjects,
    };

    // Update database
    const { data, error } = await supabase
      .from("children")
      .update({ legend_data: updatedData as any })
      .eq("id", userId)
      .select("legend_data")
      .single();

    if (error) throw error;

    return {
      success: true,
      data: data.legend_data as unknown as LegendData,
      message: showcase
        ? "Project now visible on profile"
        : "Project hidden from profile",
    };
  } catch (error: any) {
    console.error("Toggle project showcase error:", error);
    return {
      success: false,
      error: error.message,
      message: "Failed to update project",
    };
  }
};

/**
 * Delete a project
 */
export const deleteProject = async (
  userId: string,
  projectId: string,
): Promise<APIResponse<LegendData>> => {
  try {
    // Get current legend_data
    const { data: user, error: fetchError } = await supabase
      .from("children")
      .select("legend_data")
      .eq("id", userId)
      .single();

    if (fetchError) throw fetchError;

    const currentData =
      (user.legend_data as unknown as LegendData) || initializeLegendData();

    // Remove project
    const updatedProjects = (currentData.showcase_projects || []).filter(
      (proj) => proj.id !== projectId,
    );

    const updatedData: LegendData = {
      ...currentData,
      showcase_projects: updatedProjects,
    };

    // Update database
    const { data, error } = await supabase
      .from("children")
      .update({ legend_data: updatedData as any })
      .eq("id", userId)
      .select("legend_data")
      .single();

    if (error) throw error;

    return {
      success: true,
      data: data.legend_data as unknown as LegendData,
      message: "Project deleted",
    };
  } catch (error: any) {
    console.error("Delete project error:", error);
    return {
      success: false,
      error: error.message,
      message: "Failed to delete project",
    };
  }
};

/**
 * Update profile customization (theme color, visible sections)
 */
export const updateCustomization = async (
  userId: string,
  customization: Partial<LegendData["profile_customization"]>,
): Promise<APIResponse<LegendData>> => {
  try {
    // Get current legend_data
    const { data: user, error: fetchError } = await supabase
      .from("children")
      .select("legend_data")
      .eq("id", userId)
      .single();

    if (fetchError) throw fetchError;

    const currentData =
      (user.legend_data as unknown as LegendData) || initializeLegendData();

    // Merge customization
    const updatedData: LegendData = {
      ...currentData,
      profile_customization: {
        ...currentData.profile_customization,
        ...customization,
      },
    };

    // Update database
    const { data, error } = await supabase
      .from("children")
      .update({ legend_data: updatedData as any })
      .eq("id", userId)
      .select("legend_data")
      .single();

    if (error) throw error;

    return {
      success: true,
      data: data.legend_data as unknown as LegendData,
      message: "Profile customization updated!",
    };
  } catch (error: any) {
    console.error("Update customization error:", error);
    return {
      success: false,
      error: error.message,
      message: "Failed to update customization",
    };
  }
};

/**
 * Increment profile visit counter
 */
export const incrementProfileVisits = async (
  userId: string,
): Promise<APIResponse<number>> => {
  try {
    // Get current legend_data
    const { data: user, error: fetchError } = await supabase
      .from("children")
      .select("legend_data")
      .eq("id", userId)
      .single();

    if (fetchError) throw fetchError;

    const currentData =
      (user.legend_data as unknown as LegendData) || initializeLegendData();
    const currentVisits = currentData.stats_cache?.profile_visits || 0;

    // Increment visits
    const updatedData: LegendData = {
      ...currentData,
      stats_cache: {
        profile_visits: currentVisits + 1,
        last_updated: new Date().toISOString(),
      },
    };

    // Update database
    const { error } = await supabase
      .from("children")
      .update({ legend_data: updatedData as any })
      .eq("id", userId);

    if (error) throw error;

    return {
      success: true,
      data: currentVisits + 1,
    };
  } catch (error: any) {
    console.error("Increment profile visits error:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Export all functions
export default {
  getLegendData,
  updateBio,
  updateShowcaseBadges,
  addProject,
  toggleProjectShowcase,
  deleteProject,
  updateCustomization,
  incrementProfileVisits,
};
